/**
 * 发品 / 模板币种来源分流：
 * - Tiktok 半托/全托：publish/standard 的 currencyType（按店铺市场返回，如 USD/GBP/EUR/JPY）
 * - Tiktok 自运营：站点列表币种（若已接入）；否则同样回退 publish/standard
 *
 * 注意：Tiktok 没有 Shein query-publish-fill-in-standard。
 */
import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import { extractPublishCurrencies, pickPublishCurrency } from '@/tiktok-platform/utils/tiktokPublishFillInStandard';
import { tiktokShopCapabilities } from '@/tiktok-platform/utils/tiktokShopCapabilities';
import {
  extractSiteListCurrencies,
  pickSiteListCurrency,
} from '@/tiktok-platform/utils/tiktokSiteList';

/** 中文 key；展示时经 fixedUi */
export const FILL_IN_CURRENCY_EMPTY_WARNING = '未从 Tiktok 发品规范解析到可用币种，供货价可能无法提交';
export const SITE_LIST_CURRENCY_EMPTY_WARNING = '未从店铺站点列表解析到可用币种，售价可能无法提交';

function resolveCaps(shopCaps, shopType) {
  if (shopCaps && typeof shopCaps === 'object' && 'usesPriceInfo' in shopCaps) {
    return shopCaps;
  }
  return tiktokShopCapabilities(shopType);
}

export function usesSiteListCurrency(shopCapsOrType) {
  return Boolean(resolveCaps(shopCapsOrType, shopCapsOrType)?.usesPriceInfo);
}

/**
 * @returns {{
 *   source: 'site-list' | 'fill-in-standard',
 *   currencies: { code: string, name: string }[],
 *   code: string,
 *   emptyWarning: string,
 * }}
 */
export function resolveShopPublishCurrencies({
  shopType = '',
  shopCaps = null,
  siteList = null,
  fillInStandard = null,
  preferred = '',
  selectedSites = [],
} = {}) {
  const caps = resolveCaps(shopCaps, shopType);
  if (caps?.usesPriceInfo) {
    const currencies = extractSiteListCurrencies(siteList);
    if (currencies.length) {
      return {
        source: 'site-list',
        currencies,
        code: pickSiteListCurrency(siteList, { preferred, selectedSites }),
        emptyWarning: '',
      };
    }
    // Tiktok 站点列表未接入时，回退 goods.add 可用币种，避免误报 Shein 发布规范
  }
  const currencies = extractPublishCurrencies(fillInStandard);
  return {
    source: 'fill-in-standard',
    currencies,
    code: pickPublishCurrency(currencies, preferred),
    emptyWarning: currencies.length ? '' : fixedUi(FILL_IN_CURRENCY_EMPTY_WARNING),
  };
}

/**
 * 提交前校验表单币种是否落在当前店铺合法来源里。
 * Tiktok：publish/standard 按店铺市场返回币种；规范未返回时不拦。
 * 自运营：优先站点列表，未接入则回退 publish/standard。
 */
export function validateShopPublishCurrency(form = {}, {
  shopType = '',
  shopCaps = null,
  siteList = null,
  fillInStandard = null,
  standardRaw = null,
} = {}) {
  const resolved = resolveShopPublishCurrencies({
    shopType: shopType || form?.shopType,
    shopCaps,
    siteList: siteList || form?.siteList,
    fillInStandard: fillInStandard || standardRaw,
    preferred: form?.currency,
    selectedSites: form?.selectedSites,
  });
  const cur = String(form?.currency || '').trim();
  if (resolved.source === 'site-list') {
    if (!resolved.currencies.length) {
      return {
        ok: false,
        message: resolved.emptyWarning || fixedUi(SITE_LIST_CURRENCY_EMPTY_WARNING),
        section: 'sales',
        field: 'currency',
        ...resolved,
      };
    }
    if (cur && !resolved.currencies.some((row) => String(row.code).toUpperCase() === cur.toUpperCase())) {
      return {
        ok: false,
        message: fixedUiTpl(
          '售价币种「{0}」不在店铺可售站点币种中（可用：{1}）',
          cur,
          resolved.currencies.map((row) => row.code).join(fixedUi('、')),
        ),
        section: 'sales',
        field: 'currency',
        ...resolved,
      };
    }
    return { ok: true, ...resolved };
  }
  if (resolved.currencies.length && cur
    && !resolved.currencies.some((row) => String(row.code).toUpperCase() === cur.toUpperCase())) {
    return {
      ok: false,
      message: fixedUiTpl(
        '供货价币种「{0}」不在 Tiktok 发品规范允许列表中（可用：{1}）',
        cur,
        resolved.currencies.map((row) => row.code).join(fixedUi('、')),
      ),
      section: 'sales',
      field: 'currency',
      ...resolved,
    };
  }
  return { ok: true, ...resolved };
}

/**
 * 自运营站点快照无币种时再拉一次平台站点列表。
 * @param {{ refreshSiteList?: () => Promise<{ siteList?: object } | object> }} opts
 */
export async function loadShopPublishCurrencies({
  shopType = '',
  shopCaps = null,
  siteList = null,
  fillInStandard = null,
  preferred = '',
  selectedSites = [],
  refreshSiteList,
} = {}) {
  let resolvedSiteList = siteList ?? null;
  const caps = resolveCaps(shopCaps, shopType);
  if (
    caps?.usesPriceInfo
    && !extractSiteListCurrencies(resolvedSiteList).length
    && typeof refreshSiteList === 'function'
  ) {
    try {
      const refreshed = await refreshSiteList();
      if (refreshed != null) {
        resolvedSiteList = refreshed.siteList ?? refreshed;
      }
    } catch {
      // 保留快照，由 emptyWarning 提示
    }
  }
  return {
    siteList: resolvedSiteList,
    ...resolveShopPublishCurrencies({
      shopCaps: caps,
      shopType,
      siteList: resolvedSiteList,
      fillInStandard,
      preferred,
      selectedSites,
    }),
  };
}
